/**
 * SafewordAffordance — Contract §9 fixed safeword access for standalone-layout pages.
 *
 * PillarLayout pages already expose safeword via the avatar dropdown + mobile menu
 * (PillarLayout.tsx:219, :251). Pages that use standalone layouts (no app bar) have
 * no safeword entry point. This component is the fix: a fixed, persistent pill that
 * lives in the BOTTOM-LEFT corner on every such page.
 *
 * Design invariants:
 *  - BOTTOM-LEFT only — bottom-right is owned by QuickActionsProvider (CLAUDE.md
 *    "NO PAGE-LOCAL FABs"). Never move this to bottom-right.
 *  - z-[50]: above page content, below modals/sheets (z-[200]) and AppLock (z-[10001]).
 *  - safe-area-inset-left + safe-area-inset-bottom for notched devices (standalone
 *    pages must handle insets manually per CLAUDE.md "PWA safe-area on public pages").
 *  - Touch target ≥ 44 px × 44 px (CLAUDE.md UI rule).
 *  - Elevation per contract §7: 1px rgba(255,255,255,.06) border in dark, no shadows.
 *  - Tailwind + var(--theme-*) only — no new CSS file.
 */

import React from 'react';
import { Link } from 'react-router-dom';

/**
 * A small safety icon — a shield silhouette.
 * Inline SVG keeps the bundle lean (no icon library dependency) and ensures
 * the icon is always present regardless of network conditions.
 */
function SafetyIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      {/* Shield outline with a heart/check inside — universally recognised as safety */}
      <path d="M12 2L4 5v6c0 5.25 3.47 10.15 8 11.36C16.53 21.15 20 16.25 20 11V5l-8-3z" />
    </svg>
  );
}

/**
 * SafewordAffordance
 *
 * Mount on any authenticated standalone-layout page that lacks PillarLayout.
 * No props required — the destination (/safeword) and position are fixed.
 */
export function SafewordAffordance() {
  return (
    <Link
      to="/safeword"
      aria-label="Open safeword page"
      data-testid="safeword-affordance"
      /*
       * Positioning:
       *   fixed bottom-left, safe-area aware.
       *   pl/pb via Tailwind arbitrary values using env() for notched devices.
       *
       * Touch target: min-w/min-h of 44px via Tailwind (matches CLAUDE.md ≥44px rule).
       *
       * Visual:
       *   bg-[var(--theme-bg-elevated)] — card-level surface (contract §7 elevation stop 3)
       *   border — default Tailwind 1px; colour handled by border-[rgba(255,255,255,.06)]
       *   in dark via [data-theme="dark"] class scoping (see below).
       *   text-[var(--theme-text-secondary)] — discreet; not primary to avoid fighting
       *   the amber accent law (contract §2) but still readable.
       *   rounded-full — pill shape per contract §7 Pill/tag.
       *   text-xs — 12px; label "Safeword" is readable without dominating the viewport.
       *   no-underline, gap-1.5 for icon + label spacing.
       *
       * z-[50]: floats over page content.
       *   Navbar: z-[100]  (above this affordance — correct)
       *   Sheets: z-[200]  (above — correct; safeword still accessible pre-sheet)
       *   Modals: z-[200]  (above — correct)
       *   Takeover: z-[300] (above — correct)
       *   AppLock: z-[10001] (above — correct; SafewordModal is z-[10002] for app-lock flow)
       */
      className={[
        // Fixed position — bottom-left
        'fixed z-[50]',
        // Safe-area insets: Tailwind's pb/pl can't accept env() directly,
        // so we use inline style for the bottom/left offset (see style prop below).
        // The className carries the visual treatment only.

        // Layout: flex row, centred, pill shape
        'flex items-center gap-1.5',
        'px-3 py-2.5',
        'min-w-[44px] min-h-[44px]',
        'rounded-full',

        // Surface: elevated background + subtle border
        'bg-[var(--theme-bg-elevated)]',
        // Light mode: standard border
        'border border-[var(--theme-border-light)]',
        // Dark mode: contract §7 1px rgba(255,255,255,.06) border override
        'dark:border-[rgba(255,255,255,0.06)]',

        // Typography
        'text-xs font-medium',
        'text-[var(--theme-text-secondary)]',
        'no-underline',

        // Interaction
        'cursor-pointer',
        'transition-colors duration-150',
        'hover:bg-[var(--theme-bg-tertiary)]',
        'hover:text-[var(--theme-text-primary)]',

        // Focus ring for keyboard users (amber per contract §2)
        'focus-visible:outline-none',
        'focus-visible:ring-2',
        'focus-visible:ring-[var(--theme-primary)]',
        'focus-visible:ring-offset-1',
      ].join(' ')}
      style={{
        // Safe-area insets — must be inline because Tailwind's pb/pl variants
        // don't accept CSS env() function values.
        bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
        left: 'calc(16px + env(safe-area-inset-left, 0px))',
      }}
    >
      <SafetyIcon />
      <span>Safeword</span>
    </Link>
  );
}
